import React from "react";
import CDNImage from "@ui-kit/media/CDNImage/CDNImage.web";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";
import { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import { ProductModal } from "../products/ProductModal.web";

export interface AttractionProductItemProps {
  product: ProductTypeSerializer;
  venue: NestedPublicVenueSerializer;
  isLoading: boolean;
  isBasketUpdating: boolean;
}

export const AttractionProductItem: React.FC<AttractionProductItemProps> = ({
  product,
  venue,
  isLoading,
  isBasketUpdating,
}) => {
  const { t } = useTranslation("purchases");
  const defaultImage = formatRelativeStaticPath("images/default-banner.png");
  const skeletonText = "skeletonText";

  const cardImage = product?.image;

  // attraction modal variables
  const {
    isOpen: isOpenProductModal,
    onOpen: onOpenProductModal,
    onClose: onCloseProductModal,
  } = useDisclosure();

  const productName = product?.name ?? t("product");

  const priceRange = product?.product_attributes
    ? getPriceRange({
        items: product.product_attributes,
        allInPricing: Boolean(venue.all_in_pricing_is_enabled),
        venueCurrency: venue.currency,
      })
    : null;

  return (
    <>
      <ProductModal
        isOpen={isOpenProductModal}
        onClose={onCloseProductModal}
        selectedAttractionProduct={product}
        hasAllInPricing={Boolean(venue.all_in_pricing_is_enabled)}
        venue={venue}
        isBasketUpdating={isBasketUpdating}
      />
      <Box
        flexGrow={2}
        width="100%"
        height="100%"
        justifyContent="center"
        p="2xs"
        // TODO: Add hover opacity effect as component variant or use _hover pseudo prop
        // eslint-disable-next-line showpass-custom/prefer-semantic-props
        css={{
          "&:hover img": {
            opacity: 0.5,
          },
        }}
        onClick={() => {
          if (!isLoading) {
            onOpenProductModal();
          }
        }}
      >
        <Flex
          cursor="pointer"
          direction="column"
          width="100%"
          maxWidth="100%"
          tabIndex={0}
          as="button"
          textAlign="left"
          aria-label={
            priceRange
              ? t("View details for {{productName}} priced at {{priceRange}}", {
                  productName,
                  priceRange,
                })
              : t("View details for {{productName}}", {
                  productName,
                })
          }
          _focusVisible={{
            boxShadow: "outline",
            outlineOffset: "2px",
          }}
        >
          <Skeleton borderRadius="md" isLoaded={!isLoading}>
            <Flex
              borderRadius="md"
              overflow="hidden"
              width="100%"
              height="100%"
              display="block"
              aspectRatio="1 / 1"
            >
              <CDNImage
                src={cardImage ?? defaultImage}
                alt={t("Product image")}
                height={220}
                width={220}
              />
            </Flex>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} mt="xs">
            <Text
              fontWeight="bold"
              fontSize={{ base: "sm", lg: "xl" }}
              color="fg"
              textOverflow="ellipsis"
              display="-webkit-box"
              overflow="hidden"
              whiteSpace="normal"
              // TODO: Create LineClamp component or add lineClamp prop to Text
              // eslint-disable-next-line showpass-custom/prefer-semantic-props
              css={{
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
              }}
            >
              {product?.name ?? skeletonText}
            </Text>
          </Skeleton>
          <Skeleton isLoaded={!isLoading} mt="xs">
            {product?.product_attributes && (
              <Text fontSize={{ base: "xs", lg: "lg" }}>
                {getPriceRange({
                  items: product.product_attributes,
                  allInPricing: Boolean(venue.all_in_pricing_is_enabled),
                  venueCurrency: venue.currency,
                }) ?? skeletonText}
              </Text>
            )}
          </Skeleton>
        </Flex>
      </Box>
    </>
  );
};

export default AttractionProductItem;
